/**
 * Custom exception for location-related errors
 */
class LocationServiceException {
  constructor(message, code) {
    this.message = message;
    this.code = code;
  }

  toString() {
    return `LocationServiceException: ${this.message} (Code: ${this.code})`;
  }
}

// Browser error codes for geolocation
const GEOLOCATION_ERROR_CODES = {
  PERMISSION_DENIED: 1,
  POSITION_UNAVAILABLE: 2,
  TIMEOUT: 3,
  LOCATION_DISABLED: 4
};

/**
 * A service class to handle all location-related operations
 */
class LocationService {
  constructor() {
    this._lastPosition = null;
    this._positionController = null;
  }

  /**
   * Initialize location services and request necessary permissions
   */
  async initialize() {
    try {
      // Check if geolocation is supported
      if (!navigator.geolocation) {
        throw new LocationServiceException(
          'Geolocation is not supported by this browser.',
          'NOT_SUPPORTED'
        );
      }

      // For now, we'll assume initialization is successful
      // In a real app, we might want to request permission here
    } catch (error) {
      throw new LocationServiceException(
        `Failed to initialize location service: ${error.message}`,
        'INIT_ERROR'
      );
    }
  }

  /**
   * Get the current location
   */
  async getCurrentLocation() {
    try {
      await this.initialize();
      return new Promise((resolve, reject) => {
        // First try with high accuracy
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            const accuracy = position.coords.accuracy;
            this._lastPosition = { lat, lng, accuracy };
            resolve({ lat, lng, accuracy });
          },
          (error) => {
            console.warn('High accuracy location failed, trying low accuracy...', error);
            // If high accuracy fails, try low accuracy
            navigator.geolocation.getCurrentPosition(
              (position) => {
                const lat = position.coords.latitude;
                const lng = position.coords.longitude;
                const accuracy = position.coords.accuracy;
                this._lastPosition = { lat, lng, accuracy };
                resolve({ lat, lng, accuracy });
              },
              (errorLow) => {
                let code = 'GET_LOCATION_ERROR';
                if (errorLow.code === GEOLOCATION_ERROR_CODES.PERMISSION_DENIED) {
                  code = 'PERMISSION_DENIED';
                } else if (errorLow.code === GEOLOCATION_ERROR_CODES.POSITION_UNAVAILABLE) {
                  code = 'POSITION_UNAVAILABLE';
                } else if (errorLow.code === GEOLOCATION_ERROR_CODES.TIMEOUT) {
                  code = 'TIMEOUT';
                }
                reject(new LocationServiceException(
                  `Failed to get current location: ${errorLow.message}`,
                  code
                ));
              },
              {
                enableHighAccuracy: false,
                timeout: 10000,
                maximumAge: 60000
              }
            );
          },
          {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 60000
          }
        );
      });
    } catch (error) {
      throw new LocationServiceException(
        `Failed to get current location: ${error.message}`,
        'GET_LOCATION_ERROR'
      );
    }
  }

  /**
   * Get a stream of location updates
   */
  getPositionStream(options = {}) {
    const {
      accuracy = 'medium',
      interval = 5000, // 5 seconds
      distanceFilter = 0
    } = options;

    try {
      // Check if geolocation is supported
      if (!navigator.geolocation) {
        throw new LocationServiceException(
          'Geolocation is not supported by this browser.',
          'NOT_SUPPORTED'
        );
      }

      // Create a new stream controller
      const controller = new EventTarget();

      // Start watching position
      const watchId = navigator.geolocation.watchPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          const accuracy = position.coords.accuracy;
          this._lastPosition = { lat, lng, accuracy };
          controller.dispatchEvent(new CustomEvent('data', { detail: { lat, lng, accuracy } }));
        },
        (error) => {
          let code = 'STREAM_ERROR';
          if (error.code === GEOLOCATION_ERROR_CODES.PERMISSION_DENIED) {
            code = 'PERMISSION_DENIED';
          } else if (error.code === GEOLOCATION_ERROR_CODES.POSITION_UNAVAILABLE) {
            code = 'POSITION_UNAVAILABLE';
          } else if (error.code === GEOLOCATION_ERROR_CODES.TIMEOUT) {
            code = 'TIMEOUT';
          }
          controller.dispatchEvent(new CustomEvent('error', {
            detail: new LocationServiceException(
              `Error in position stream: ${error.message}`,
              code
            )
          }));
        },
        {
          enableHighAccuracy: accuracy === 'high',
          timeout: 10000,
          maximumAge: 60000,
          distanceFilter
        }
      );

      // Store the watch ID for potential cleanup
      this._watchId = watchId;

      return controller;
    } catch (error) {
      throw new LocationServiceException(
        `Failed to get position stream: ${error.message}`,
        'STREAM_ERROR'
      );
    }
  }

  /**
   * Calculate distance between two points (simplified implementation)
   */
  calculateDistance(point1, point2) {
    // Simple implementation using Haversine formula
    const R = 6371; // Earth radius in km
    const dLat = this.toRad(point2.lat - point1.lat);
    const dLon = this.toRad(point2.lng - point1.lng);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(point1.lat)) * Math.cos(this.toRad(point2.lat)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * Convert degrees to radians
   */
  toRad(degrees) {
    return degrees * (Math.PI / 180);
  }

  /**
   * Clean up resources
   */
  dispose() {
    if (this._watchId) {
      navigator.geolocation.clearWatch(this._watchId);
    }
  }
}

// Singleton instance for easy access
const locationService = new LocationService();

export { LocationService, locationService, LocationServiceException };
